/**
 * Seed corpus for Garnet Core Studio — ported from the back-office handoff `data.jsx`.
 * Human-authored, cited, versioned. Conditions are written in plain legal language for
 * the analyst, not code. Nothing here is generated; the one intentionally-uncited draft
 * (EU-DualUse-Annex1-9A) exists to demonstrate the "nothing uncited ships" gate.
 */
import type {
  AuditEntry,
  Client,
  CoverageEntry,
  Domain,
  GoldenCase,
  Jurisdiction,
  Meta,
  Person,
  ReleaseRecord,
  Role,
  RoleKey,
  Rule,
  RuleDiff,
  SourceOfLaw,
} from './types.js';

export const JURISDICTIONS: Jurisdiction[] = [
  { code: 'EU', name: 'European Union', sub: '27 member states', flag: '27' },
  { code: 'UK', name: 'United Kingdom', sub: 'Great Britain', flag: 'GB' },
  { code: 'IE', name: 'Ireland', sub: 'Éire', flag: 'IE' },
  { code: 'IN', name: 'India', sub: 'Bhārat', flag: 'IN' },
  { code: 'AE', name: 'United Arab Emirates', sub: 'Emirates', flag: 'AE' },
];

export const DOMAINS: Domain[] = [
  { key: 'medical', name: 'Medical device', short: 'Medical' },
  { key: 'automotive', name: 'Automotive type-approval', short: 'Automotive' },
  { key: 'electronics', name: 'Electronics', short: 'Electronics' },
  { key: 'corporate', name: 'Corporate & ownership', short: 'Corporate' },
  { key: 'tax_customs', name: 'Tax & customs', short: 'Tax/Customs' },
  { key: 'sanctions', name: 'Sanctions & export', short: 'Sanctions' },
  { key: 'insurance', name: 'Insurance', short: 'Insurance' },
];

function cov(
  state: CoverageEntry['state'],
  rules: number,
  topics: number,
  gate: CoverageEntry['gate'],
  owner: string,
  ncr: number,
  abr: number,
): CoverageEntry {
  return { state, rules, topics, gate, owner, noCoverage: ncr, abstain: abr };
}

export const COVERAGE: Record<string, CoverageEntry> = {
  'EU:medical': cov('covered', 184, 12, 'answer', 'R. Mehta', 0.4, 3.1),
  'EU:automotive': cov('covered', 96, 9, 'answer', 'L. Byrne', 1.2, 4.0),
  'EU:electronics': cov('covered', 71, 7, 'answer', 'L. Byrne', 2.1, 5.5),
  'EU:corporate': cov('thin', 22, 5, 'abstain', 'S. Okafor', 18.0, 9.2),
  'EU:tax_customs': cov('thin', 31, 6, 'abstain', 'S. Okafor', 14.3, 7.8),
  'EU:sanctions': cov('thin', 19, 4, 'abstain', 'D. Cole', 21.5, 11.0),
  'EU:insurance': cov('empty', 0, 0, 'abstain', '—', 100, 0),

  'UK:medical': cov('covered', 142, 11, 'answer', 'R. Mehta', 0.9, 3.6),
  'UK:automotive': cov('thin', 34, 6, 'abstain', 'L. Byrne', 12.0, 8.1),
  'UK:electronics': cov('thin', 28, 5, 'abstain', 'L. Byrne', 16.2, 9.0),
  'UK:corporate': cov('thin', 18, 4, 'abstain', 'S. Okafor', 22.4, 10.2),
  'UK:tax_customs': cov('thin', 24, 5, 'abstain', 'S. Okafor', 17.0, 8.4),
  'UK:sanctions': cov('thin', 16, 3, 'abstain', 'D. Cole', 26.0, 12.5),
  'UK:insurance': cov('empty', 0, 0, 'abstain', '—', 100, 0),

  'IE:medical': cov('covered', 88, 9, 'answer', 'R. Mehta', 1.4, 4.2),
  'IE:automotive': cov('thin', 21, 4, 'abstain', 'L. Byrne', 19.0, 9.5),
  'IE:electronics': cov('thin', 17, 4, 'abstain', 'L. Byrne', 23.0, 10.1),
  'IE:corporate': cov('covered', 41, 6, 'answer', 'S. Okafor', 3.2, 5.0),
  'IE:tax_customs': cov('thin', 27, 5, 'abstain', 'S. Okafor', 15.5, 8.0),
  'IE:sanctions': cov('empty', 0, 0, 'abstain', '—', 100, 0),
  'IE:insurance': cov('thin', 12, 3, 'abstain', 'S. Okafor', 28.0, 13.0),

  'IN:medical': cov('thin', 46, 7, 'abstain', 'R. Mehta', 11.0, 7.0),
  'IN:automotive': cov('thin', 38, 6, 'abstain', 'L. Byrne', 13.5, 8.8),
  'IN:electronics': cov('empty', 0, 0, 'abstain', '—', 100, 0),
  'IN:corporate': cov('thin', 14, 4, 'abstain', 'S. Okafor', 24.0, 11.0),
  'IN:tax_customs': cov('thin', 20, 5, 'abstain', 'S. Okafor', 19.5, 9.2),
  'IN:sanctions': cov('empty', 0, 0, 'abstain', '—', 100, 0),
  'IN:insurance': cov('empty', 0, 0, 'abstain', '—', 100, 0),

  'AE:medical': cov('thin', 19, 4, 'abstain', 'R. Mehta', 22.0, 10.5),
  'AE:automotive': cov('empty', 0, 0, 'abstain', '—', 100, 0),
  'AE:electronics': cov('empty', 0, 0, 'abstain', '—', 100, 0),
  'AE:corporate': cov('thin', 9, 3, 'abstain', 'S. Okafor', 31.0, 14.0),
  'AE:tax_customs': cov('thin', 11, 3, 'abstain', 'S. Okafor', 27.0, 12.0),
  'AE:sanctions': cov('empty', 0, 0, 'abstain', '—', 100, 0),
  'AE:insurance': cov('empty', 0, 0, 'abstain', '—', 100, 0),
};

export const SOURCES: SourceOfLaw[] = [
  { id: 'src-mdr-a8', citation: 'Regulation (EU) 2017/745 — Annex VIII', kind: 'annex', juris: 'EU', title: 'MDR · Classification rules', effectiveFrom: '2021-05-26', url: 'eur-lex.europa.eu/eli/reg/2017/745', lastChecked: '2026-06-08', watch: 'stable', deps: ['EU-MDR-AnnexVIII-R12', 'EU-MDR-AnnexVIII-R11', 'EU-MDR-AnnexVIII-R8', 'EU-MDR-AnnexVIII-R6', 'EU-MDR-AnnexVIII-R1'], change: null },
  { id: 'src-mdr', citation: 'Regulation (EU) 2017/745', kind: 'regulation', juris: 'EU', title: 'Medical Devices Regulation (MDR)', effectiveFrom: '2021-05-26', url: 'eur-lex.europa.eu/eli/reg/2017/745', lastChecked: '2026-06-08', watch: 'changed', deps: ['EU-MDR-AnnexVIII-R12', 'EU-MDR-Art120-Transitional'], change: 'Regulation (EU) 2023/607 extended the transitional acceptance dates — Article 120 amended.' },
  { id: 'src-ivdr', citation: 'Regulation (EU) 2017/746', kind: 'regulation', juris: 'EU', title: 'In-Vitro Diagnostic Regulation (IVDR)', effectiveFrom: '2022-05-26', url: 'eur-lex.europa.eu/eli/reg/2017/746', lastChecked: '2026-05-30', watch: 'monitoring', deps: [], change: null },
  { id: 'src-ukmdr', citation: 'UK Medical Devices Regulations 2002 (SI 2002/618)', kind: 'statute', juris: 'UK', title: 'UK MDR 2002 · Part II', effectiveFrom: '2002-06-13', url: 'legislation.gov.uk/uksi/2002/618', lastChecked: '2026-06-01', watch: 'stable', deps: ['UK-MDR2002-Part2-Reg7'], change: null },
  { id: 'src-unece-13h', citation: 'UN Regulation No. 13-H', kind: 'regulation', juris: 'EU', title: 'Braking — passenger cars & light goods', effectiveFrom: '2021-09-01', url: 'unece.org/transport/vehicle-regulations', lastChecked: '2026-06-05', watch: 'stable', deps: ['UNECE-R13H-Brake-Class'], change: null },
  { id: 'src-unece-48', citation: 'UN Regulation No. 48', kind: 'regulation', juris: 'EU', title: 'Installation of lighting', effectiveFrom: '2023-01-01', url: 'unece.org/transport/vehicle-regulations', lastChecked: '2026-05-28', watch: 'changed', deps: ['UNECE-R48-Lighting'], change: 'Supplement 16 amended the adaptive driving-beam provisions.' },
  { id: 'src-unece-107', citation: 'UN Regulation No. 107', kind: 'regulation', juris: 'EU', title: 'Buses & coaches — bodywork', effectiveFrom: '2022-06-01', url: 'unece.org/transport/vehicle-regulations', lastChecked: '2026-04-12', watch: 'stable', deps: [], change: null },
  { id: 'src-red', citation: 'Directive 2014/53/EU', kind: 'directive', juris: 'EU', title: 'Radio Equipment Directive — Article 3', effectiveFrom: '2016-06-13', url: 'eur-lex.europa.eu/eli/dir/2014/53', lastChecked: '2026-05-20', watch: 'stable', deps: ['EU-RED-Art3-Essential'], change: null },
  { id: 'src-emc', citation: 'Directive 2014/30/EU', kind: 'directive', juris: 'EU', title: 'Electromagnetic Compatibility', effectiveFrom: '2016-04-20', url: 'eur-lex.europa.eu/eli/dir/2014/30', lastChecked: '2026-03-30', watch: 'stable', deps: [], change: null },
  { id: 'src-dualuse', citation: 'Regulation (EU) 2021/821 — Annex I', kind: 'regulation', juris: 'EU', title: 'Dual-Use · Category 9 (Aerospace)', effectiveFrom: '2021-09-09', url: 'eur-lex.europa.eu/eli/reg/2021/821', lastChecked: '2026-06-02', watch: 'monitoring', deps: ['EU-DualUse-Annex1-9A'], change: null },
  { id: 'src-mdcg', citation: 'MDCG 2021-24', kind: 'guidance', juris: 'EU', title: 'Guidance on classifying medical devices', effectiveFrom: '2021-10-01', url: 'health.ec.europa.eu/mdcg', lastChecked: '2026-05-15', watch: 'stable', deps: ['EU-MDR-AnnexVIII-R11'], change: null },
  { id: 'src-mdr-a9', citation: 'Regulation (EU) 2017/745 — Annex IX', kind: 'annex', juris: 'EU', title: 'MDR · Conformity assessment (quality management)', effectiveFrom: '2021-05-26', url: 'eur-lex.europa.eu/eli/reg/2017/745', lastChecked: '2026-06-08', watch: 'stable', deps: ['EU-MDR-AnnexIX-NBCert'], change: null },
  { id: 'src-mdr-a14', citation: 'Regulation (EU) 2017/745 — Annex XIV', kind: 'annex', juris: 'EU', title: 'MDR · Clinical evaluation', effectiveFrom: '2021-05-26', url: 'eur-lex.europa.eu/eli/reg/2017/745', lastChecked: '2026-06-08', watch: 'stable', deps: ['EU-MDR-AnnexXIV-ClinEval'], change: null },
  { id: 'src-nando', citation: 'NANDO — designated Notified Bodies', kind: 'register', juris: 'EU', title: 'Notified Body designation & scope database', effectiveFrom: '2021-05-26', url: 'ec.europa.eu/growth/tools-databases/nando', lastChecked: '2026-06-04', watch: 'stable', deps: ['EU-MDR-NB-ScopeCheck'], change: null },
];

function vr(version: number, date: string, author: string, approver: string | null, note: string) {
  return { version, date, author, approver, note };
}

export const RULES: Rule[] = [
  {
    ruleId: 'EU-MDR-AnnexVIII-R12', juris: 'EU', domain: 'medical', topic: 'classification',
    outputType: 'classification', status: 'published', version: 3,
    title: 'Active devices that administer or remove a medicine',
    condition: {
      kind: 'conditions',
      factors: [
        { name: 'Active device', note: 'yes / no' },
        { name: 'Intended purpose', note: 'administers or removes a medicine' },
        { name: 'Potentially hazardous manner', note: 'yes / no' },
      ],
      clauses: [
        { when: 'The device is active, is intended to administer or remove a medicine, and does so in a potentially hazardous manner', then: 'Class IIb' },
        { when: 'The device is active and administers or removes a medicine, but not in a hazardous manner', then: 'Class IIa' },
        { when: 'Any other case', then: 'Abstain — no rule applies' },
      ],
    },
    output: { type: 'classification', value: 'Class IIb', detail: 'Active devices intended to administer or remove a medicine, where the way they do so could be hazardous — for example an infusion pump or a dialysis machine.' },
    citations: ['src-mdr-a8', 'src-mdr'],
    effectiveFrom: '2021-05-26', effectiveTo: null,
    authoredBy: 'R. Mehta', approvedBy: 'A. Doyle', rationale: 'Infusion pumps and dialysis equipment are Class IIb under Rule 12 where dosing is safety-critical.',
    versions: [
      vr(3, '2026-03-02', 'R. Mehta', 'A. Doyle', 'Aligned the "hazardous manner" test to MDCG 2021-24, §5.2. No change of class.'),
      vr(2, '2024-11-18', 'R. Mehta', 'A. Doyle', 'Added the "remove a medicine" branch (dialysis).'),
      vr(1, '2021-05-26', 'P. Nair', 'A. Doyle', 'First written when the MDR took effect.'),
    ],
    tests: [
      { input: 'Volumetric infusion pump', expect: 'Class IIb', status: 'pass' },
      { input: 'Haemodialysis machine', expect: 'Class IIb', status: 'pass' },
      { input: 'Powered nebuliser (no medicine)', expect: 'Class IIa', status: 'pass' },
    ],
  },
  {
    ruleId: 'EU-MDR-AnnexVIII-R8', juris: 'EU', domain: 'medical', topic: 'classification',
    outputType: 'classification', status: 'published', version: 2,
    title: 'Implantable and long-term surgical devices',
    condition: {
      kind: 'conditions',
      factors: [
        { name: 'Implantable', note: 'yes / no' },
        { name: 'Surgically invasive', note: 'yes / no' },
        { name: 'Duration of use', note: 'transient / short-term / long-term' },
        { name: 'Where it sits in the body', note: 'heart or central nervous system / teeth / elsewhere' },
      ],
      clauses: [
        { when: 'Implantable, and in contact with the heart or central nervous system', then: 'Class III' },
        { when: 'Implantable, or surgically invasive and used long-term', then: 'Class IIb' },
        { when: 'In contact with the teeth', then: 'Class IIa' },
        { when: 'Any other case', then: 'Abstain — no rule applies' },
      ],
    },
    output: { type: 'classification', value: 'Class IIb / III', detail: 'Implantable devices are Class IIb, rising to Class III where the device is in contact with the heart, central circulation or central nervous system.' },
    citations: ['src-mdr-a8'],
    effectiveFrom: '2021-05-26', effectiveTo: null,
    authoredBy: 'R. Mehta', approvedBy: 'A. Doyle', rationale: 'Implants default to IIb, rising to III for heart or central-nervous-system contact under Rule 8.',
    versions: [
      vr(2, '2025-06-10', 'R. Mehta', 'A. Doyle', 'Separated heart / central-nervous-system contact into the Class III branch.'),
      vr(1, '2021-05-26', 'P. Nair', 'A. Doyle', 'First written.'),
    ],
    tests: [
      { input: 'Total hip prosthesis', expect: 'Class IIb', status: 'pass' },
      { input: 'Coronary stent', expect: 'Class III', status: 'pass' },
      { input: 'Dental filling material', expect: 'Class IIa', status: 'pass' },
    ],
  },
  {
    ruleId: 'EU-MDR-AnnexVIII-R11', juris: 'EU', domain: 'medical', topic: 'classification',
    outputType: 'classification', status: 'published', version: 4,
    title: 'Software that informs diagnosis or treatment',
    condition: {
      kind: 'table',
      factors: [
        { name: 'Software', note: 'yes / no' },
        { name: 'Information it provides', note: 'diagnosis / treatment / monitoring' },
        { name: 'Possible impact on the patient', note: 'death or irreversible / serious / moderate' },
      ],
      columns: ['Information is for', 'Possible impact', 'Classification'],
      rows: [
        ['Diagnosis or treatment', 'Death or irreversible harm', 'Class III'],
        ['Diagnosis or treatment', 'Serious deterioration', 'Class IIb'],
        ['Diagnosis or treatment', 'Moderate', 'Class IIa'],
        ['Monitoring', 'Serious deterioration', 'Class IIb'],
        ['Monitoring', 'Moderate', 'Class IIa'],
        ['Any other case', '—', 'Class IIa'],
      ],
    },
    output: { type: 'classification', value: 'Class IIa–III', detail: 'Software is classified by how important its information is to a clinical decision and how seriously the patient could be affected.' },
    citations: ['src-mdr-a8', 'src-mdcg'],
    effectiveFrom: '2021-05-26', effectiveTo: null,
    authoredBy: 'R. Mehta', approvedBy: 'A. Doyle', rationale: 'Rule 11 as explained by MDCG 2021-24. A table is the clearest form for the impact matrix.',
    versions: [
      vr(4, '2026-01-22', 'R. Mehta', 'A. Doyle', 'Added the two monitoring lines; cited MDCG 2021-24.'),
      vr(3, '2025-04-09', 'R. Mehta', 'A. Doyle', 'Moved moderate impact to Class IIa.'),
      vr(2, '2023-08-30', 'P. Nair', 'A. Doyle', 'Rewritten as a decision table.'),
      vr(1, '2021-05-26', 'P. Nair', 'A. Doyle', 'First written.'),
    ],
    tests: [
      { input: 'Radiology triage software (serious)', expect: 'Class IIb', status: 'pass' },
      { input: 'Wellness step counter', expect: 'Class IIa', status: 'pass' },
    ],
  },
  {
    ruleId: 'EU-MDR-AnnexVIII-R6', juris: 'EU', domain: 'medical', topic: 'classification',
    outputType: 'classification', status: 'published', version: 1,
    title: 'Surgical devices used only transiently',
    condition: {
      kind: 'conditions',
      factors: [
        { name: 'Surgically invasive', note: 'yes / no' },
        { name: 'Duration of use', note: 'transient / short-term / long-term' },
      ],
      clauses: [
        { when: 'Surgically invasive and used only transiently', then: 'Class IIa' },
        { when: 'Any other case', then: 'Abstain — no rule applies' },
      ],
    },
    output: { type: 'classification', value: 'Class IIa', detail: 'Surgically invasive devices intended for transient use. Higher-numbered rules handle the exceptions.' },
    citations: ['src-mdr-a8'],
    effectiveFrom: '2021-05-26', effectiveTo: null,
    authoredBy: 'P. Nair', approvedBy: 'A. Doyle', rationale: 'Baseline Class IIa for transient surgical use; exceptions are caught by later rules.',
    versions: [vr(1, '2021-05-26', 'P. Nair', 'A. Doyle', 'First written.')],
    tests: [{ input: 'Absorbable suture', expect: 'Class IIa', status: 'pass' }],
  },
  {
    ruleId: 'EU-MDR-AnnexVIII-R1', juris: 'EU', domain: 'medical', topic: 'classification',
    outputType: 'classification', status: 'published', version: 1,
    title: 'Devices that are not invasive (default)',
    condition: {
      kind: 'conditions',
      factors: [
        { name: 'Invasive', note: 'no / through a body opening / surgically' },
        { name: 'Active device', note: 'yes / no' },
      ],
      clauses: [
        { when: 'Not invasive and not active', then: 'Class I' },
        { when: 'Any other case', then: 'Abstain — a more specific rule should apply' },
      ],
    },
    output: { type: 'classification', value: 'Class I', detail: 'The default for devices that are neither invasive nor active, unless a more specific rule applies.' },
    citations: ['src-mdr-a8'],
    effectiveFrom: '2021-05-26', effectiveTo: null,
    authoredBy: 'P. Nair', approvedBy: 'A. Doyle', rationale: 'Rule 1 default; overridden by Rules 2–4 where they apply.',
    versions: [vr(1, '2021-05-26', 'P. Nair', 'A. Doyle', 'First written.')],
    tests: [{ input: 'Surgical drape', expect: 'Class I', status: 'pass' }],
  },
  {
    ruleId: 'UK-MDR2002-Part2-Reg7', juris: 'UK', domain: 'medical', topic: 'classification',
    outputType: 'classification', status: 'published', version: 2,
    title: 'UKCA classification of general medical devices',
    condition: {
      kind: 'conditions',
      factors: [{ name: 'Equivalent EU class', note: 'I / IIa / IIb / III' }],
      clauses: [
        { when: 'An equivalent EU class has been determined', then: 'The same class (the UK retains the EU rules)' },
        { when: 'Any other case', then: 'Abstain — no rule applies' },
      ],
    },
    output: { type: 'classification', value: 'Mirrors the EU class', detail: 'The UK keeps the retained classification under SI 2002/618 while UK MDR reform is pending.' },
    citations: ['src-ukmdr'],
    effectiveFrom: '2021-01-01', effectiveTo: null,
    authoredBy: 'R. Mehta', approvedBy: 'A. Doyle', rationale: 'UK divergence is not yet enacted, so classification mirrors the retained rules.',
    versions: [
      vr(2, '2025-09-14', 'R. Mehta', 'A. Doyle', 'Noted pending UK MDR reform; no change of behaviour.'),
      vr(1, '2021-01-01', 'P. Nair', 'A. Doyle', 'First written after the transition period.'),
    ],
    tests: [{ input: 'Device that is Class IIb in the EU', expect: 'Class IIb', status: 'pass' }],
  },
  {
    ruleId: 'UNECE-R13H-Brake-Class', juris: 'EU', domain: 'automotive', topic: 'requirement',
    outputType: 'requirement', status: 'published', version: 2,
    title: 'Which braking rules apply, by vehicle type',
    condition: {
      kind: 'table',
      factors: [{ name: 'Vehicle type', note: 'passenger car / light goods / heavier' }],
      columns: ['Vehicle type', 'Braking requirement'],
      rows: [
        ['Passenger car (M1)', 'UN Regulation 13-H applies'],
        ['Light goods (N1)', 'UN Regulation 13-H applies'],
        ['Heavier vehicles (buses, trucks)', 'Outside this rule — Abstain'],
      ],
    },
    output: { type: 'requirement', value: 'UN R13-H braking', detail: 'Applies to passenger cars and light goods vehicles. Heavier vehicles fall under UN Regulation 13, which is not yet held.' },
    citations: ['src-unece-13h'],
    effectiveFrom: '2021-09-01', effectiveTo: null,
    authoredBy: 'L. Byrne', approvedBy: 'A. Doyle', rationale: 'R13-H is the braking rule for cars and light goods. Heavier vehicles are deliberately left out so the system abstains until UN R13 is written.',
    versions: [
      vr(2, '2025-12-03', 'L. Byrne', 'A. Doyle', 'Made the abstain explicit for heavier vehicles.'),
      vr(1, '2021-09-01', 'L. Byrne', 'A. Doyle', 'First written.'),
    ],
    tests: [
      { input: 'Passenger car', expect: 'UN R13-H applies', status: 'pass' },
      { input: 'Light van', expect: 'UN R13-H applies', status: 'pass' },
      { input: 'Rigid truck', expect: 'Abstain', status: 'pass' },
    ],
  },
  {
    ruleId: 'UNECE-R48-Lighting', juris: 'EU', domain: 'automotive', topic: 'requirement',
    outputType: 'requirement', status: 'in_review', version: 5,
    title: 'Installation of lighting',
    condition: {
      kind: 'conditions',
      factors: [
        { name: 'Vehicle type', note: 'passenger / goods / trailer' },
        { name: 'Adaptive driving beam fitted', note: 'yes / no' },
      ],
      clauses: [
        { when: 'A passenger or goods vehicle fitted with an adaptive driving beam', then: 'UN Regulation 48 §6, plus the adaptive-beam provisions (Supplement 16)' },
        { when: 'Any passenger, goods or trailer vehicle', then: 'UN Regulation 48 §6 (baseline)' },
        { when: 'Any other case', then: 'Abstain — no rule applies' },
      ],
    },
    output: { type: 'requirement', value: 'UN R48 §6', detail: 'Lighting installation, including adaptive driving-beam provisions where one is fitted.' },
    citations: ['src-unece-48'],
    effectiveFrom: '2023-01-01', effectiveTo: null,
    authoredBy: 'L. Byrne', approvedBy: null, rationale: 'Re-opened by the Supplement 16 amendment to the adaptive driving beam; draft is with the reviewer.',
    versions: [
      vr(5, '2026-06-09', 'L. Byrne', null, 'Draft: add the Supplement 16 adaptive-beam provisions. With reviewer.'),
      vr(4, '2024-02-11', 'L. Byrne', 'A. Doyle', 'Baseline installation requirements.'),
    ],
    tests: [{ input: 'Car with an adaptive driving beam', expect: 'UN R48 §6 + adaptive beam', status: 'pending' }],
  },
  {
    ruleId: 'EU-RED-Art3-Essential', juris: 'EU', domain: 'electronics', topic: 'requirement',
    outputType: 'requirement', status: 'published', version: 1,
    title: 'Essential requirements for radio equipment',
    condition: {
      kind: 'conditions',
      factors: [{ name: 'Radio equipment', note: 'yes / no' }],
      clauses: [
        { when: 'The product is radio equipment', then: 'Must meet Article 3: health & safety, electromagnetic compatibility, and efficient use of the radio spectrum' },
        { when: 'Any other case', then: 'Abstain — no rule applies' },
      ],
    },
    output: { type: 'requirement', value: 'RED Article 3', detail: 'Health and safety, electromagnetic compatibility, efficient spectrum use, and any further requirements designated under Article 3(3).' },
    citations: ['src-red'],
    effectiveFrom: '2016-06-13', effectiveTo: null,
    authoredBy: 'L. Byrne', approvedBy: 'A. Doyle', rationale: 'Baseline applicability of the Radio Equipment Directive.',
    versions: [vr(1, '2016-06-13', 'L. Byrne', 'A. Doyle', 'First written.')],
    tests: [{ input: 'Bluetooth sensor module', expect: 'RED Article 3 applies', status: 'pass' }],
  },
  {
    ruleId: 'EU-DualUse-Annex1-9A', juris: 'EU', domain: 'sanctions', topic: 'applicability',
    outputType: 'applicability', status: 'draft', version: 1,
    title: 'Aerospace dual-use items (Category 9)',
    condition: {
      kind: 'conditions',
      factors: [{ name: 'Control-list entry', note: '9A001 / 9A012 / other' }],
      clauses: [
        { when: 'The item is listed under 9A001 or 9A012', then: 'An export authorisation is required' },
        { when: 'Any other case', then: 'Abstain — no rule applies' },
      ],
    },
    output: { type: 'applicability', value: 'Export authorisation required', detail: 'Dual-use control over listed aerospace and propulsion items.' },
    citations: [], // intentionally uncited — a draft that cannot be published
    effectiveFrom: '2021-09-09', effectiveTo: null,
    authoredBy: 'D. Cole', approvedBy: null, rationale: 'Draft outline for Category 9. The exact Annex I sub-entry still needs to be confirmed before a citation can be added.',
    versions: [vr(1, '2026-06-11', 'D. Cole', null, 'Draft. Citation still outstanding — cannot move forward.')],
    tests: [],
  },
  {
    ruleId: 'EU-MDR-Art120-Transitional', juris: 'EU', domain: 'medical', topic: 'value',
    outputType: 'value', status: 'published', version: 2,
    title: 'Transitional acceptance date',
    condition: {
      kind: 'conditions',
      factors: [{ name: 'Device class', note: 'III / IIb / IIa / I' }],
      clauses: [
        { when: 'Class III, or an implantable Class IIb', then: 'Acceptance date: 31 December 2027' },
        { when: 'Class IIb, IIa or I', then: 'Acceptance date: 31 December 2028' },
        { when: 'Any other case', then: 'Abstain — no rule applies' },
      ],
    },
    output: { type: 'value', value: '31 Dec 2027 / 2028', detail: 'The extended transitional acceptance dates introduced by Regulation (EU) 2023/607.' },
    citations: ['src-mdr'],
    effectiveFrom: '2023-03-20', effectiveTo: null,
    authoredBy: 'R. Mehta', approvedBy: 'A. Doyle', rationale: 'Holds the amended Article 120 dates so consumers get a stated value, not a guess.',
    versions: [
      vr(2, '2023-03-20', 'R. Mehta', 'A. Doyle', 'Updated to the Regulation (EU) 2023/607 dates.'),
      vr(1, '2021-05-26', 'P. Nair', 'A. Doyle', 'Original 2024 / 2025 dates.'),
    ],
    tests: [{ input: 'Class III device', expect: '31 December 2027', status: 'pass' }],
  },
  {
    ruleId: 'IE-CRO-BeneficialOwner', juris: 'IE', domain: 'corporate', topic: 'requirement',
    outputType: 'requirement', status: 'published', version: 1,
    title: 'Beneficial-ownership register filing',
    condition: {
      kind: 'conditions',
      factors: [
        { name: 'Entity type', note: 'limited company / plc / branch' },
        { name: 'Largest beneficial owner', note: 'percentage held' },
      ],
      clauses: [
        { when: 'A beneficial owner holds more than 25%', then: 'Must file to the Register of Beneficial Ownership within 5 months of incorporation' },
        { when: 'Any other case', then: 'Abstain — no rule applies' },
      ],
    },
    output: { type: 'requirement', value: 'RBO filing required', detail: 'Beneficial owners holding more than 25% must be filed to the Central Register of Beneficial Ownership.' },
    citations: ['src-ukmdr'],
    effectiveFrom: '2019-06-22', effectiveTo: null,
    authoredBy: 'S. Okafor', approvedBy: 'A. Doyle', rationale: 'SI 110/2019, transposing the beneficial-ownership rules of the 4th Anti-Money-Laundering Directive.',
    versions: [vr(1, '2019-06-22', 'S. Okafor', 'A. Doyle', 'First written.')],
    tests: [{ input: 'Limited company, 30% owner', expect: 'RBO filing required', status: 'pass' }],
  },
  {
    ruleId: 'EU-MDR-AnnexIX-NBCert', juris: 'EU', domain: 'medical', topic: 'evidence',
    outputType: 'evidence', status: 'published', version: 1,
    title: 'Notified Body certificate required above Class I',
    condition: {
      kind: 'conditions',
      factors: [
        { name: 'Device class', note: 'I / IIa / IIb / III' },
        { name: 'Sterile or measuring', note: 'yes / no' },
      ],
      clauses: [
        { when: 'The device is Class IIa, IIb or III', then: 'Require: Notified Body conformity certificate' },
        { when: 'The device is Class I and is sterile or has a measuring function', then: 'Require: Notified Body certificate (limited scope)' },
        { when: 'Any other case', then: 'Abstain — no rule applies' },
      ],
    },
    output: { type: 'evidence', value: 'Notified Body conformity certificate', trigger: 'Classification result (read from the technical file)', detail: 'Anything above plain Class I needs a Notified Body to certify conformity. The certificate must be on file before a Declaration of Conformity is accepted.' },
    citations: ['src-mdr-a9'],
    effectiveFrom: '2021-05-26', effectiveTo: null,
    authoredBy: 'R. Mehta', approvedBy: 'A. Doyle', rationale: 'Once the class is known, the conformity route follows. This rule turns a classification into the next document to ask for.',
    versions: [vr(1, '2026-02-14', 'R. Mehta', 'A. Doyle', 'First written.')],
    tests: [
      { input: 'Class III device', expect: 'NB certificate required', status: 'pass' },
      { input: 'Sterile Class I device', expect: 'NB certificate (limited)', status: 'pass' },
      { input: 'Plain Class I device', expect: 'Abstain', status: 'pass' },
    ],
  },
  {
    ruleId: 'EU-MDR-NB-ScopeCheck', juris: 'EU', domain: 'medical', topic: 'evidence',
    outputType: 'evidence', status: 'published', version: 2,
    title: 'Confirm the Notified Body’s scope covers the device',
    condition: {
      kind: 'conditions',
      factors: [
        { name: 'Notified Body certificate on file', note: 'yes / no' },
        { name: 'Certificate names this device type', note: 'yes / no' },
      ],
      clauses: [
        { when: 'A Notified Body certificate has been provided', then: 'Require: the Notified Body’s designation scope (NANDO), to confirm it covers this device type and code' },
        { when: 'Any other case', then: 'Abstain — no rule applies' },
      ],
    },
    output: { type: 'evidence', value: 'Notified Body designation scope (NANDO listing)', trigger: 'Notified Body conformity certificate', detail: 'A certificate is only as good as the body’s designated scope. After reading the certificate, the next document is the NANDO listing that proves the body was designated for this device’s codes.' },
    citations: ['src-nando'],
    effectiveFrom: '2021-05-26', effectiveTo: null,
    authoredBy: 'R. Mehta', approvedBy: 'A. Doyle', rationale: 'A worked example of one document leading to the next: certificate → the body’s scope. Without the scope, the certificate cannot be relied on.',
    versions: [
      vr(2, '2026-05-20', 'R. Mehta', 'A. Doyle', 'Added the device-code match factor.'),
      vr(1, '2026-02-14', 'R. Mehta', 'A. Doyle', 'First written.'),
    ],
    tests: [
      { input: 'Certificate from a designated body', expect: 'NANDO scope required', status: 'pass' },
      { input: 'No certificate yet', expect: 'Abstain', status: 'pass' },
    ],
  },
  {
    ruleId: 'EU-MDR-AnnexXIV-ClinEval', juris: 'EU', domain: 'medical', topic: 'evidence',
    outputType: 'evidence', status: 'in_review', version: 1,
    title: 'Clinical evaluation report required for higher-risk devices',
    condition: {
      kind: 'conditions',
      factors: [
        { name: 'Implantable', note: 'yes / no' },
        { name: 'Device class', note: 'I / IIa / IIb / III' },
      ],
      clauses: [
        { when: 'The device is implantable or Class III', then: 'Require: Clinical Evaluation Report (Annex XIV)' },
        { when: 'Any other case', then: 'Abstain — no rule applies' },
      ],
    },
    output: { type: 'evidence', value: 'Clinical Evaluation Report', trigger: 'Classification result', detail: 'Implantable and Class III devices need a clinical evaluation. After the class is set, this report is the next document to request.' },
    citations: ['src-mdr-a14'],
    effectiveFrom: '2021-05-26', effectiveTo: null,
    authoredBy: 'R. Mehta', approvedBy: null, rationale: 'Draft pending review — the implantable-IIb edge needs a reviewer’s call before it ships.',
    versions: [vr(1, '2026-06-10', 'R. Mehta', null, 'Draft. With reviewer.')],
    tests: [
      { input: 'Coronary stent (Class III)', expect: 'Clinical evaluation required', status: 'pass' },
      { input: 'Surgical drape (Class I)', expect: 'Abstain', status: 'pending' },
    ],
  },
];

export const GOLDEN_MEDICAL: GoldenCase[] = [
  { name: 'Surgical drape', inp: { invasive: 'none', active: false, duration: 'transient', implantable: false, contact: 'none', purpose: [], hazardous: false }, expect: 'Class I' },
  { name: 'Volumetric infusion pump', inp: { invasive: 'none', active: true, duration: 'transient', implantable: false, contact: 'none', purpose: ['administer_medicine'], hazardous: true }, expect: 'Class IIb' },
  { name: 'Total hip prosthesis', inp: { invasive: 'surgical', active: false, duration: 'long_term', implantable: true, contact: 'none', purpose: [], hazardous: false }, expect: 'Class IIb' },
  { name: 'Coronary stent', inp: { invasive: 'surgical', active: false, duration: 'long_term', implantable: true, contact: 'heart_cns', purpose: [], hazardous: false }, expect: 'Class III' },
  { name: 'Absorbable suture', inp: { invasive: 'surgical', active: false, duration: 'transient', implantable: false, contact: 'none', purpose: [], hazardous: false }, expect: 'Class IIa' },
  { name: 'Nanomaterial wound dressing', inp: { invasive: 'none', active: false, duration: 'transient', implantable: false, contact: 'none', purpose: [], hazardous: false, nanomaterial: true }, expect: 'Abstain' },
];

export const GOLDEN_AUTO: GoldenCase[] = [
  { name: 'Passenger car', inp: { use: 'passenger', seats: 5, massKg: 1400 }, expect: 'Passenger car (M1)' },
  { name: 'Light van', inp: { use: 'goods', seats: 2, massKg: 2800 }, expect: 'Light goods (N1)' },
  { name: 'Minibus (12 seats)', inp: { use: 'passenger', seats: 12, massKg: 4200 }, expect: 'Minibus (M2)' },
  { name: 'Rigid truck', inp: { use: 'goods', seats: 2, massKg: 12000 }, expect: 'Abstain' },
];

export const AUDIT: AuditEntry[] = [
  { id: 'a-094', ts: '2026-06-11 14:22', actor: 'D. Cole', action: 'draft created', target: 'EU-DualUse-Annex1-9A', from: null, to: 'v1', note: 'New draft — Category 9 aerospace. Not yet cited.', affected: 0, kind: 'draft' },
  { id: 'a-093', ts: '2026-06-09 11:08', actor: 'L. Byrne', action: 'draft revised', target: 'UNECE-R48-Lighting', from: 'v4', to: 'v5', note: 'Added the Supplement 16 adaptive-beam provisions; sent for review.', affected: 0, kind: 'review' },
  { id: 'a-092', ts: '2026-06-08 16:40', actor: 'Source watch', action: 'law changed', target: 'src-unece-48', from: null, to: null, note: 'Supplement 16 detected — 1 rule flagged to revisit.', affected: 1, kind: 'watch' },
  { id: 'a-091', ts: '2026-03-02 09:15', actor: 'A. Doyle', action: 'rule published', target: 'EU-MDR-AnnexVIII-R12', from: 'v2', to: 'v3', note: 'Approved. "Hazardous manner" test aligned to MDCG 2021-24.', affected: 1840, kind: 'publish' },
  { id: 'a-090', ts: '2026-01-22 13:50', actor: 'A. Doyle', action: 'rule published', target: 'EU-MDR-AnnexVIII-R11', from: 'v3', to: 'v4', note: 'Approved. Monitoring lines added.', affected: 612, kind: 'publish' },
  { id: 'a-089', ts: '2025-12-03 10:31', actor: 'A. Doyle', action: 'rule published', target: 'UNECE-R13H-Brake-Class', from: 'v1', to: 'v2', note: 'Approved. Abstain made explicit for heavier vehicles.', affected: 220, kind: 'publish' },
  { id: 'a-088', ts: '2025-11-15 17:02', actor: 'A. Doyle', action: 'release cut', target: 'release 2025.11.1', from: 'release 2025.09.2', to: 'release 2025.11.1', note: 'Quarterly release. 14 rule changes.', affected: 4200, kind: 'release' },
  { id: 'a-087', ts: '2025-09-14 08:44', actor: 'A. Doyle', action: 'rule published', target: 'UK-MDR2002-Part2-Reg7', from: 'v1', to: 'v2', note: 'Approved. Noted pending UK MDR reform.', affected: 410, kind: 'publish' },
  { id: 'a-086', ts: '2025-06-10 12:19', actor: 'A. Doyle', action: 'rule published', target: 'EU-MDR-AnnexVIII-R8', from: 'v1', to: 'v2', note: 'Approved. Heart / central-nervous-system contact → Class III.', affected: 980, kind: 'publish' },
  { id: 'a-085', ts: '2025-02-28 15:00', actor: 'A. Doyle', action: 'release rolled back', target: 'release 2025.02.1', from: 'release 2025.02.1', to: 'release 2025.01.3', note: 'Rolled back — a class regression on the software rule. The case was added to the reference set.', affected: 1200, kind: 'rollback' },
];

export const DIFFS: Record<string, RuleDiff> = {
  'EU-MDR-AnnexVIII-R12': { from: 'v2', to: 'v3', date: '2026-03-02', author: 'R. Mehta', approver: 'A. Doyle', lines: [
    { t: 'ctx', s: 'Conditions:' },
    { t: 'ctx', s: '  When active, and administers or removes a medicine,' },
    { t: 'del', s: '    and the manner is hazardous' },
    { t: 'add', s: '    and does so in a potentially hazardous manner   (MDCG 2021-24, §5.2)' },
    { t: 'ctx', s: '    → Class IIb' },
    { t: 'ctx', s: '' },
    { t: 'ctx', s: 'Source of law:' },
    { t: 'ctx', s: '  Regulation (EU) 2017/745 — Annex VIII' },
    { t: 'add', s: '  + MDCG 2021-24 (guidance)' },
  ] },
  'EU-MDR-AnnexVIII-R8': { from: 'v1', to: 'v2', date: '2025-06-10', author: 'R. Mehta', approver: 'A. Doyle', lines: [
    { t: 'ctx', s: 'Conditions:' },
    { t: 'add', s: '  When implantable and in contact with the heart or central nervous system' },
    { t: 'add', s: '    → Class III' },
    { t: 'ctx', s: '  When implantable, or surgically invasive and used long-term' },
    { t: 'del', s: '    → Class IIb   (previously caught every implant)' },
    { t: 'add', s: '    → Class IIb   (heart / CNS now handled above)' },
    { t: 'ctx', s: '  When in contact with the teeth → Class IIa' },
  ] },
  'EU-MDR-AnnexVIII-R11': { from: 'v3', to: 'v4', date: '2026-01-22', author: 'R. Mehta', approver: 'A. Doyle', lines: [
    { t: 'ctx', s: 'Decision table:' },
    { t: 'ctx', s: '  Diagnosis or treatment · death or irreversible → Class III' },
    { t: 'ctx', s: '  Diagnosis or treatment · serious → Class IIb' },
    { t: 'ctx', s: '  Diagnosis or treatment · moderate → Class IIa' },
    { t: 'add', s: '  Monitoring · serious → Class IIb' },
    { t: 'add', s: '  Monitoring · moderate → Class IIa' },
    { t: 'ctx', s: '  Any other case → Class IIa' },
    { t: 'ctx', s: '' },
    { t: 'ctx', s: 'Source of law:' },
    { t: 'add', s: '  + MDCG 2021-24 (guidance)' },
  ] },
  'UNECE-R13H-Brake-Class': { from: 'v1', to: 'v2', date: '2025-12-03', author: 'L. Byrne', approver: 'A. Doyle', lines: [
    { t: 'ctx', s: 'Decision table:' },
    { t: 'ctx', s: '  Passenger car (M1) → UN R13-H applies' },
    { t: 'ctx', s: '  Light goods (N1) → UN R13-H applies' },
    { t: 'del', s: '  Heavier vehicles → UN R13 (referenced, unverified)' },
    { t: 'add', s: '  Heavier vehicles → Outside this rule → Abstain' },
  ] },
};

export const RELEASES: ReleaseRecord[] = [
  { name: 'release 2026.06.1', date: '2026-06-01', rules: 854, changes: 11, status: 'current' },
  { name: 'release 2026.03.2', date: '2026-03-15', rules: 851, changes: 9, status: 'pinned' },
  { name: 'release 2025.11.1', date: '2025-11-15', rules: 838, changes: 14, status: 'archived' },
];

export const META: Meta = {
  release: 'release 2026.06.1',
  citationCompleteness: 99.2,
  determinism: 100,
  abstainRate: 6.4,
  noCoverageRate: 12.1,
  servingLatency: 41,
  driftLatency: '3.2 days',
  totalRules: 854,
  client: 'Garnet Advisor · classification engine',
};

export const CLIENTS: Client[] = [
  { id: 'meridian', name: 'Meridian Devices', sector: 'Medical device manufacturer', domains: ['medical'], jurisdictions: ['EU', 'UK', 'IE'], release: 'release 2026.06.1', owner: 'R. Mehta', since: '2024' },
  { id: 'cortex', name: 'Cortex Diagnostics', sector: 'Diagnostics & connected devices', domains: ['medical', 'electronics'], jurisdictions: ['EU', 'UK'], release: 'release 2026.06.1', owner: 'R. Mehta', since: '2025' },
  { id: 'brightway', name: 'Brightway Mobility', sector: 'Vehicle manufacturer', domains: ['automotive', 'electronics'], jurisdictions: ['EU', 'UK', 'IE'], release: 'release 2026.03.2', owner: 'L. Byrne', since: '2025' },
  { id: 'halberd', name: 'Halberd Aerospace', sector: 'Aerospace & export control', domains: ['sanctions', 'electronics'], jurisdictions: ['EU', 'UK', 'AE'], release: 'release 2026.06.1', owner: 'D. Cole', since: '2026' },
  { id: 'atlas', name: 'Atlas Components', sector: 'Electronics & import/export', domains: ['electronics', 'tax_customs'], jurisdictions: ['EU', 'IN', 'AE'], release: 'release 2026.06.1', owner: 'S. Okafor', since: '2026' },
];

export const ROLES: Record<RoleKey, Role> = {
  viewer: { label: 'Viewer', rank: 0, can: {}, blurb: 'Read everything. Change nothing.' },
  author: { label: 'Author', rank: 1, can: { author: true }, blurb: 'Draft and edit rules; send them for review.' },
  reviewer: { label: 'Reviewer', rank: 2, can: { author: true, publish: true }, blurb: 'Approve and publish rules.' },
  admin: { label: 'Admin', rank: 3, can: { author: true, publish: true, gate: true, clients: true }, blurb: 'Approve, publish, and set the coverage gate.' },
};

export const PEOPLE: Record<RoleKey, Person> = {
  viewer: { name: 'J. Okonkwo', title: 'Audit', initials: 'JO' },
  author: { name: 'R. Mehta', title: 'Regulatory analyst', initials: 'RM' },
  reviewer: { name: 'A. Doyle', title: 'Legal reviewer', initials: 'AD' },
  admin: { name: 'P. Nair', title: 'Knowledge-base admin', initials: 'PN' },
};
